import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { Alert, Button, Checkbox, DatePicker, Dropdown, Input, InputNumber, Popover, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCircle,
  faCircleCheck,
  faDownload,
  faEllipsisVertical,
  faFileExcel,
  faListUl,
  faLock,
  faLockOpen,
  faNoteSticky,
  faPaperPlane,
  faPrint,
  faRotateLeft,
  faRotateRight,
  faSlidersH,
  faTrashCan,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  ADJUSTMENT_NOTE_MAX_LENGTH,
  INTERNAL_NOTE_MAX_LENGTH,
  INVOICE_CHECK_LABELS,
  ONE_TIME_BONUS_MAX_PERCENT,
  TAX_TYPE_LABELS,
  formatDateDDMMYYYY,
  formatWithholdingPct,
  hugCoursePay,
  planAllowsSending,
} from '@salaries/shared';
import { useAuth } from '../../auth/AuthContext';
import type {
  BoxMechanism,
  InvoiceCheckIssue,
  InvoiceCheckStatus,
  OneTimeBonusMode,
  PayrollLineStatus,
  PayrollReportRow,
  PeriodSelection,
  RecomputeDiff,
  UnitRunMeta,
} from '@salaries/shared';
import {
  NO_BRANCH,
  NO_BRANCH_LABEL,
  PNL_UNIT_NONE,
  branchesOf,
  enabledMechanisms,
  hasBranchBreakdown,
  rowForBranch,
} from '@salaries/shared';
import {
  clearLineInvoice,
  downloadDetailedPayrollExcel,
  downloadPayrollExcel,
  getDefaultPeriod,
  getInternalNotes,
  streamPayrollReport,
  type PayrollReportProgress,
  getSingleSendStatus,
  openArboxProfile,
  revertLine,
  sendBatch,
  sendPayroll,
  updateDeduction,
  updateInternalNote,
  updateOneTimeBonus,
} from '../../api/payroll';
import { confirmModal } from '../../components/confirmModal';
import { Money, formatMoney } from '../../components/Money';
import { EmptyState } from '../../components/EmptyState';
import { Loader, loaderIndicator } from '../../components/Loader';
import { useTableScrollY } from '../../components/useTableScrollY';
import { PayrollBulkBar } from './PayrollBulkBar';
import { ReceiptPreview } from './ReceiptPreview';
import { PayrollDetailDrawer } from './PayrollDetailDrawer';
import { RecomputeDiffModal } from './RecomputeDiffModal';
import { StaffSalaryEditDrawer } from '../staff-settings/StaffSalaryEditDrawer';
import {
  awaitRunJob,
  finalizeLines,
  lockRun,
  recomputePreview,
  reopenRun,
} from '../../api/payrollRun';
import { parseRoles } from './roles';
import { ColumnsIcon } from '../../components/icons';
import { exportPayrollCsv, exportPayrollPrint } from './payrollExport';

// The report works one full calendar month at a time. Which month it OPENS on is
// decided server-side (GET /payroll/default-period — see the rules in
// @salaries/shared/default-period): last month while it's still being worked on,
// this month once it's closed / done / already started, or from the 21st onwards.
// This stays as the fallback for when that call fails.
function lastMonth(): { year: number; month: number } {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/** Comparable ordinal for a (year, month) pair — for "is this month in the past?". */
const monthIndex = (m: { year: number; month: number }) => m.year * 12 + m.month;

// Searchable multi-select filter dropdown (mirrors ArboxExpenses). MUST be a
// real component (not an inline render fn) — antd mounts the filterDropdown
// lazily/conditionally, so its hooks have to live in their own instance.
// Rendering hooks inside the parent's render crashes with "Rendered fewer
// hooks than expected" when a column is hidden via the columns picker.
function MultiSelectFilterDropdown({
  options,
  withSearch,
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: {
  options: { text: string; value: string }[];
  withSearch: boolean;
  setSelectedKeys: (keys: React.Key[]) => void;
  selectedKeys: React.Key[];
  confirm: () => void;
  clearFilters?: () => void;
}) {
  const [draft, setDraft] = useState<React.Key[]>(selectedKeys || []);
  const [search, setSearch] = useState('');
  useEffect(() => setDraft(selectedKeys || []), [selectedKeys]);
  const filtered =
    withSearch && search
      ? options.filter((o) => o.text.toLowerCase().includes(search.toLowerCase()))
      : options;
  const vals = filtered.map((o) => o.value);
  const allSel = vals.length > 0 && vals.every((v) => draft.includes(v));
  const someSel = !allSel && vals.some((v) => draft.includes(v));
  const toggleAll = () =>
    setDraft(
      allSel
        ? draft.filter((v) => !vals.includes(v as string))
        : Array.from(new Set([...draft, ...vals])),
    );
  const toggle = (v: string) =>
    setDraft(draft.includes(v) ? draft.filter((x) => x !== v) : [...draft, v]);
  const lbl = {
    fontSize: 13,
    color: '#3b3b3b',
    maxWidth: 200,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  } as const;
  return (
    <div style={{ padding: 8, minWidth: 200 }}>
      {withSearch && (
        <div style={{ paddingBottom: 8 }}>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש..." />
        </div>
      )}
      <label className="flex items-center cursor-pointer" style={{ paddingBottom: 8 }}>
        <Checkbox checked={allSel} indeterminate={someSel} onChange={toggleAll} />
        <span style={{ ...lbl, marginInlineStart: 8, flex: 1 }}>בחר הכל</span>
      </label>
      <div style={{ maxHeight: 192, overflowY: 'auto' }}>
        {filtered.map((o) => (
          <label key={o.value} className="flex items-center cursor-pointer" style={{ paddingBottom: 8 }}>
            <Checkbox checked={draft.includes(o.value)} onChange={() => toggle(o.value)} />
            <span style={{ ...lbl, marginInlineStart: 8, flex: 1 }}>{o.text}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2 justify-end mt-1">
        <Button
          size="small"
          onClick={() => {
            setDraft([]);
            setSelectedKeys([]);
            clearFilters?.();
            confirm();
          }}
        >
          איפוס
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={() => {
            setSelectedKeys(draft);
            confirm();
          }}
        >
          אישור
        </Button>
      </div>
    </div>
  );
}

function StatusLabel({ status, comment }: { status: PayrollLineStatus; comment?: string | null }) {
  if (status === 'final') return <span className="text-success-green font-semibold">סופי</span>;
  if (status === 'approved') return <span className="text-success-green">אושר</span>;
  if (status === 'needs_correction') {
    const el = <span className="text-error-red">צריך תיקונים</span>;
    // The coach's comment is viewable on hover.
    return comment ? (
      <Tooltip title={comment}>
        <span className="cursor-help underline decoration-dotted">{el}</span>
      </Tooltip>
    ) : (
      el
    );
  }
  if (status === 'sent') return <span className="text-primary">נשלח</span>;
  return <span className="text-warning-yellow">טרם נשלח</span>;
}

// Inline editor for the two hand-entered adjustments on a payroll line — the
// one-time BONUS and the one-time DEDUCTION (קנס / השתתפות בסדנה / קיזוז). They
// behave identically, so one component renders both and `kind` picks which
// fields of the row it reads and which endpoint it saves to; anything else would
// let the two drift apart on a screen where they sit side by side.
//
// Each is entered either as a ₪ amount or as a percentage of every OTHER salary
// component on this line. The ₪ / % toggle flips between the two and saves
// immediately; in percent mode the resolved ₪ (what the coach is actually paid,
// or actually loses) is shown underneath, since that's the number the operator is
// reasoning about. Both values are kept server-side, so toggling back and forth
// never discards what was typed in the other mode.
//
// The reason field below the amount is what the coach reads next to the money on
// their own payslip, so "₪300 was taken off" never arrives unexplained. It only
// appears once there's an amount to explain — an empty text box on every row of a
// 40-coach report is noise.
//
// Both belong to the ROW — the (coach, P&L unit) pair — so in a box split into
// branches each branch grants its own bonus and imposes its own deduction, on its
// own line, into its own P&L. Typing here never moves money onto another branch's
// row.
const ADJUSTMENT_UI = {
  bonus: {
    save: updateOneTimeBonus,
    error: 'שגיאה בשמירת הבונוס',
    reasonPlaceholder: 'סיבת הבונוס',
    /** A bonus adds, so its resolved ₪ reads as a plain positive amount. */
    sign: 1,
  },
  deduction: {
    save: updateDeduction,
    error: 'שגיאה בשמירת ההפחתה',
    reasonPlaceholder: 'סיבת ההפחתה',
    /** A deduction is STORED positive and always DISPLAYED negative — it's the
     *  one number on the row that takes money away. */
    sign: -1,
  },
} as const;

/** The row fields for one kind of adjustment, so the component reads one shape
 *  whichever it's rendering. `resolved` is what the engine actually applied;
 *  `amount`/`percent` are what the operator typed. */
function adjustmentOf(row: PayrollReportRow, kind: AdjustmentKind) {
  return kind === 'bonus'
    ? {
        resolved: row.one_time_bonus,
        mode: row.one_time_bonus_mode,
        amount: row.one_time_bonus_amount,
        percent: row.one_time_bonus_percent,
        note: row.one_time_bonus_note,
      }
    : {
        resolved: row.deduction,
        mode: row.deduction_mode,
        amount: row.deduction_amount,
        percent: row.deduction_percent,
        note: row.deduction_note,
      };
}

type AdjustmentKind = 'bonus' | 'deduction';

/** The private operator note on one line. Never sent to the coach — it lives
 *  outside the report rows (own query, own endpoint) — and, since it moves no
 *  money, stays editable on a closed period too. */
function InternalNoteCell({
  row,
  note,
  periodStart,
  periodEnd,
}: {
  row: PayrollReportRow;
  note: string | null;
  periodStart: string;
  periodEnd: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(note ?? '');

  const mutation = useMutation({
    mutationFn: (next: string | null) =>
      updateInternalNote({
        arbox_users_boxes_id: row.arbox_users_boxes_id,
        pnl_unit_id: row.pnl_unit_id ?? 0,
        period_start: periodStart,
        period_end: periodEnd,
        note: next,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll-internal-notes'] });
      setOpen(false);
    },
    onError: (err) => toast.error((err as Error).message || 'שגיאה בשמירת ההערה'),
  });

  const save = () => {
    const next = draft.trim() || null;
    if (next === (note ?? null)) {
      setOpen(false);
      return;
    }
    mutation.mutate(next);
  };

  return (
    <Popover
      trigger="click"
      // The column sits at the table's far (left) edge — open downward so the
      // popover isn't pushed against the viewport.
      placement="bottomLeft"
      open={open}
      onOpenChange={(v) => {
        if (v) setDraft(note ?? '');
        setOpen(v);
      }}
      title="הערה פנימית"
      content={
        <div className="flex flex-col gap-2" style={{ width: 260 }}>
          <span className="text-secondary-text" style={{ fontSize: 12 }}>
            גלויה רק לצוות הניהול — לא נשלחת לעובד/ת
          </span>
          <Input.TextArea
            autoFocus
            value={draft}
            maxLength={INTERNAL_NOTE_MAX_LENGTH}
            autoSize={{ minRows: 3, maxRows: 8 }}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button size="small" onClick={() => setOpen(false)}>
              ביטול
            </Button>
            <Button size="small" type="primary" loading={mutation.isPending} onClick={save}>
              שמירה
            </Button>
          </div>
        </div>
      }
    >
      <button
        type="button"
        aria-label={note ? 'עריכת הערה פנימית' : 'הוספת הערה פנימית'}
        className="flex items-center gap-1 w-full min-w-0 overflow-hidden text-start rounded px-1 hover:bg-grey-bg"
      >
        <FontAwesomeIcon icon={faNoteSticky} className={`shrink-0 ${note ? 'text-primary' : 'text-secondary-text'}`} />
        {note && (
          <span className="truncate min-w-0 text-main-text" style={{ fontSize: 12 }} title={note}>
            {note}
          </span>
        )}
      </button>
    </Popover>
  );
}

function AdjustmentCell({
  row,
  kind,
  periodStart,
  periodEnd,
  locked,
}: {
  row: PayrollReportRow;
  kind: AdjustmentKind;
  periodStart: string;
  periodEnd: string;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const ui = ADJUSTMENT_UI[kind];
  const stored = adjustmentOf(row, kind);
  const [mode, setMode] = useState<OneTimeBonusMode>(stored.mode);
  // The ₪ the operator entered, which the server keeps even while mode is
  // 'percent' — so flipping % → ₪ restores what was typed instead of saving a 0
  // over it. (stored.resolved is the RESOLVED amount, which in percent mode is
  // what the percentage came to, not what was entered.)
  const [amount, setAmount] = useState<number>(stored.amount);
  const [percent, setPercent] = useState<number>(stored.percent);
  const [note, setNote] = useState<string>(stored.note ?? '');
  // Only a real edit is worth a PUT. Comparing against the row can't tell "typed
  // the same number again" from "cleared it to 0" on a row whose stored value is
  // 0 — and the second one has to save.
  const [dirty, setDirty] = useState(false);

  const mutation = useMutation({
    mutationFn: (next: {
      mode: OneTimeBonusMode;
      amount: number;
      percent: number;
      note: string | null;
    }) =>
      ui.save({
        arbox_users_boxes_id: row.arbox_users_boxes_id,
        // It lands on THIS row's unit — the branch the operator is looking at —
        // not on the coach as a whole.
        pnl_unit_id: row.pnl_unit_id ?? 0,
        period_start: periodStart,
        period_end: periodEnd,
        ...next,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
      // The per-employee detail drawer caches its own copy — refresh it too so
      // the change shows without a manual page reload.
      void queryClient.invalidateQueries({ queryKey: ['payroll-detail'] });
    },
    onError: (err) => toast.error((err as Error).message || ui.error),
  });

  const signed = (v: number) => v * ui.sign;

  // Locked period: the amount is frozen into the snapshot — editing here would
  // diverge from the frozen number until a recompute, so it's read-only.
  if (locked) {
    return (
      <div className="flex flex-col items-end leading-tight">
        <Money value={signed(stored.resolved)} />
        {stored.percent > 0 && (
          <span className="text-secondary-text" style={{ fontSize: 11 }} dir="ltr">
            {stored.percent}%
          </span>
        )}
        {stored.note && (
          <span className="text-secondary-text truncate" style={{ fontSize: 11, maxWidth: 130 }} title={stored.note}>
            {stored.note}
          </span>
        )}
      </div>
    );
  }

  const isPercent = mode === 'percent';
  const send = (next: { mode: OneTimeBonusMode; amount: number; percent: number }) => {
    setDirty(false);
    mutation.mutate({ ...next, note: note.trim() || null });
  };
  const flipMode = () => {
    const next = isPercent ? 'amount' : 'percent';
    setMode(next);
    send({ mode: next, amount, percent });
  };

  // One save when focus LEAVES the cell, not on every field's blur: the operator
  // types an amount and tabs straight into the reason, and a save in between
  // would refetch the report and remount this cell out from under them (the
  // column keys the cell on its stored values — see the column definition).
  const commitIfLeaving = (e: FocusEvent<HTMLDivElement>) => {
    if (!dirty) return;
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    send({ mode, amount, percent });
  };

  // The reason only earns its space once there's money to explain — or once one
  // was already given (so it can still be edited or cleared back to nothing).
  const hasValue = (isPercent ? percent : amount) > 0 || note.length > 0;

  return (
    <div className="flex flex-col items-end gap-0.5" onBlur={commitIfLeaving}>
      <InputNumber
        min={0}
        max={isPercent ? ONE_TIME_BONUS_MAX_PERCENT : undefined}
        value={isPercent ? percent : amount}
        size="small"
        dir="ltr"
        style={{ width: 104 }}
        onChange={(v) => {
          setDirty(true);
          if (isPercent) setPercent(Number(v) || 0);
          else setAmount(Number(v) || 0);
        }}
        addonAfter={
          <Tooltip title={isPercent ? 'אחוז מהשכר — לחצו לסכום קבוע' : 'סכום קבוע — לחצו לאחוזים'}>
            <button
              type="button"
              // Don't let the toggle take focus: it lives INSIDE this cell, so a
              // focus shift would fire the cell's blur-commit with the OLD mode
              // and race the flip's own save. Keeping focus put means exactly one
              // PUT, in every browser.
              onMouseDown={(e) => e.preventDefault()}
              onClick={flipMode}
              aria-label={isPercent ? 'עבור לסכום קבוע' : 'עבור לאחוזים'}
              className="px-1 text-primary hover:text-primary-hover"
            >
              {isPercent ? '%' : '₪'}
            </button>
          </Tooltip>
        }
      />
      {hasValue && (
        <Input
          value={note}
          size="small"
          placeholder={ui.reasonPlaceholder}
          maxLength={ADJUSTMENT_NOTE_MAX_LENGTH}
          style={{ width: 104 }}
          onChange={(e) => {
            setDirty(true);
            setNote(e.target.value);
          }}
        />
      )}
      {/* What a percentage actually resolves to this period — and, for a ₪
          deduction the engine had to floor, what was actually taken. */}
      {(isPercent || (kind === 'deduction' && stored.resolved !== stored.amount)) && (
        <span className="text-secondary-text" style={{ fontSize: 11 }}>
          <Money value={signed(stored.resolved)} />
        </span>
      )}
    </div>
  );
}

// Called after a period is closed so the parent can jump to the frozen report.
export interface ClosedRunRef {
  period_start: string;
  period_end: string;
  period_label: string;
  /** 0 (PNL_UNIT_NONE) when the box doesn't split by unit. */
  pnl_unit_id: number;
  /** null when the box doesn't split by unit. */
  pnl_unit_name: string | null;
}

/** While the report is on screen, Arbox data older than this is re-pulled in the background. */
const AUTO_REFRESH_MS = 5 * 60_000;

export function PayrollReportTab({
  onPeriodClosed,
}: {
  onPeriodClosed?: (run: ClosedRunRef) => void;
} = {}) {
  const queryClient = useQueryClient();
  // Selected calendar month. Full months only. Seeded from /default-period on
  // first render (see `defaultPeriodQuery` below), so the initial value here is
  // only ever shown if that call fails.
  const [month, setMonth] = useState<{ year: number; month: number }>(lastMonth);
  // The month has settled — either the default resolved or the user picked one.
  // Until then we hold the (expensive) report request back so we never fetch a
  // month we're about to navigate away from.
  const [monthSettled, setMonthSettled] = useState(false);
  // Default to showing ALL staff (incl. zero-pay) so the report doubles as the
  // full roster for role-filtering. The toggle still lets you hide zero rows.
  // (Send / auto-send skip zero-pay staff server-side regardless — spec.)
  const [showZero, setShowZero] = useState(true);
  // Row keys are per (coach, P&L unit) so a coach split across units selects
  // independently. Composite string key "<staffId>:<unitId>".
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  // Which P&L unit the report is scoped to. undefined = the combined view
  // across every unit (the box-wide report, unchanged for a box that doesn't
  // split). A real unit id scopes the whole screen — report, lock/reopen,
  // recompute — to that unit alone, so closing סניף תל אביב never touches
  // הרצליה. Reset whenever the month changes (a unit closed for one period
  // may not exist / matter for another).
  const [unitFilter, setUnitFilter] = useState<number | undefined>(undefined);
  // Which branch (Arbox location) the report shows. A VIEW only: each row shows
  // its share of that branch, but lines are still computed, sent, approved and
  // closed per (coach, P&L unit). undefined = the whole line. NO_BRANCH = the
  // pay that belongs to no branch (base salary, bonus, …). Client-side — the
  // rows already carry the split, so changing it costs no request.
  const [branchFilter, setBranchFilter] = useState<string | undefined>(undefined);
  // The staff member whose breakdown drawer is open (null = closed). unitId is the
  // P&L-unit slice (undefined when the box doesn't split by unit).
  const [detailStaff, setDetailStaff] = useState<{
    id: number;
    name: string;
    unitId?: number;
    unitName?: string | null;
    /** Invoice amount-check verdict for this row, carried into the drawer. */
    invoiceCheckStatus?: InvoiceCheckStatus | null;
    invoiceAmount?: number | null;
    invoiceCheckIssues?: InvoiceCheckIssue[];
  } | null>(null);
  // The staff member whose salary-settings drawer is open (null = closed).
  const [salaryEditStaff, setSalaryEditStaff] = useState<{ id: number; name: string } | null>(null);
  // Recompute (corrections-on-locked-run) modal.
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  // The diff is built in the background; the modal only opens once it's here.
  const [recomputeDiff, setRecomputeDiff] = useState<RecomputeDiff | null>(null);
  // The unit scope the OPEN diff was built with (captured at request time, not
  // read live) — apply must stay within exactly what the diff showed, even if
  // the operator flips the page's unit picker while the modal is open.
  const [recomputeUnitId, setRecomputeUnitId] = useState<number | undefined>(undefined);
  // Column visibility — same persisted shape as ArboxExpenses ({dataIndex:{show}}
  // in localStorage). New columns default visible (show !== false).
  const [columnsState, setColumnsState] = useState<Record<string, { show: boolean }>>(() => {
    try {
      return JSON.parse(localStorage.getItem('salaries-columns-state') || '{}');
    } catch {
      return {};
    }
  });
  useEffect(() => {
    localStorage.setItem('salaries-columns-state', JSON.stringify(columnsState));
  }, [columnsState]);
  const [columnsPickerOpen, setColumnsPickerOpen] = useState(false);
  // Rows currently shown in the table after column filtering — antd hands us the
  // post-filter dataset via the Table's onChange (`currentDataSource`). null =
  // "no filter applied", i.e. fall back to the full `rows`. Feeds the תכלת
  // summary bar so its totals track whatever is on screen.
  const [filteredRows, setFilteredRows] = useState<PayrollReportRow[] | null>(null);

  // Header stays fixed while rows scroll. Reserve = the table's own header +
  // pagination chrome (~96px), the תכלת summary bar (~44px) and a gutter.
  const [tableWrapperRef, tableScrollY] = useTableScrollY(96 + 44 + 16);

  const selection: PeriodSelection = { kind: 'month', year: month.year, month: month.month };

  // Sending salary for approval is a seat_based-only capability; `basic` boxes
  // see the report + exports but no send affordances (server enforces too).
  const auth = useAuth();
  const canSend = planAllowsSending(auth.plan);

  // Which month to open on. Resolved once per mount; the user's own choice always
  // wins from then on (`monthSettled` gates the seeding effect below).
  const defaultPeriodQuery = useQuery({
    queryKey: ['payroll-default-period'],
    queryFn: getDefaultPeriod,
    staleTime: Infinity,
  });
  const defaultPeriod = defaultPeriodQuery.data;

  useEffect(() => {
    if (monthSettled) return;
    if (defaultPeriod) {
      setMonth({ year: defaultPeriod.year, month: defaultPeriod.month });
      setMonthSettled(true);
    } else if (defaultPeriodQuery.isError) {
      // Fall back to the historical behaviour (last month) rather than blocking.
      setMonthSettled(true);
    }
  }, [defaultPeriod, defaultPeriodQuery.isError, monthSettled]);

  // The user picking a month always beats the resolved default, even mid-flight.
  const pickMonth = (m: { year: number; month: number }) => {
    setMonth({ year: m.year, month: m.month });
    setMonthSettled(true);
    // A unit scoped for one month may not be the right lens for another —
    // don't carry it across, always land back on the combined view.
    setUnitFilter(undefined);
    setBranchFilter(undefined);
  };

  // The current month + its label, for the "jump to this month" button. Labelled
  // by the server (it knows the box's pay period); dayjs is the offline fallback.
  const currentMonth = defaultPeriod?.this_month ?? {
    year: dayjs().year(),
    month: dayjs().month() + 1,
    label: dayjs().format('MMMM YYYY'),
  };

  // Live progress of the report being streamed for `key` — pages fetched, how
  // far into the month Arbox has got, and the latest PARTIAL preview.
  const [progress, setProgress] = useState<(PayrollReportProgress & { key: string }) | null>(null);
  const reportKey = JSON.stringify([selection, unitFilter]);
  const reportQuery = useQuery({
    queryKey: ['payroll-report', selection, unitFilter],
    queryFn: ({ signal }) => {
      const key = reportKey;
      return streamPayrollReport(
        selection,
        unitFilter,
        {},
        (p) =>
          setProgress((prev) => ({
            key,
            pages: p.pages,
            through: p.through,
            data: p.data ?? (prev?.key === key ? prev.data : undefined),
          })),
        signal,
      ).finally(() => setProgress((prev) => (prev?.key === key ? null : prev)));
    },
    enabled: monthSettled,
  });

  // Cleared when its stream settles, so a matching key means one is running now
  // (the report's own load, or a refresh of it).
  const liveProgress = progress?.key === reportKey ? progress : null;
  // A partial preview is shown ONLY on a first load. A refetch keeps the
  // complete report it already has on screen rather than dropping to a
  // half-month's numbers. Partial = display only: every action is off.
  const partialData = reportQuery.data ? undefined : liveProgress?.data;
  const isPartial = partialData !== undefined;
  const data = reportQuery.data ?? partialData;
  const throughLabel = liveProgress?.through ? dayjs(liveProgress.through).format('D.M') : null;

  // ── Freshness ── The server answers from its saved copy of the Arbox rows
  // (instant) and says how old it is. While the report is on screen and the tab
  // is visible, a copy older than AUTO_REFRESH_MS is re-pulled from Arbox in the
  // background; "רענון" does the same on demand. The data on screen stays put
  // until the fresh report lands — never a drop to partial numbers.
  const [refreshingKey, setRefreshingKey] = useState<string | null>(null);
  const refreshingRef = useRef<string | null>(null);
  const autoFailedAtRef = useRef(0);
  const refreshReport = (reason: 'manual' | 'auto') => {
    const key = reportKey;
    if (refreshingRef.current === key || reportQuery.isFetching) return;
    refreshingRef.current = key;
    setRefreshingKey(key);
    const queryKey = ['payroll-report', selection, unitFilter];
    streamPayrollReport(selection, unitFilter, { refresh: true }, (p) =>
      setProgress({ key, pages: p.pages, through: p.through }),
    )
      .then((fresh) => {
        queryClient.setQueryData(queryKey, fresh);
        void queryClient.invalidateQueries({ queryKey: ['payroll-detail'] });
      })
      .catch((e: unknown) => {
        if (reason === 'manual') toast.error((e as Error).message || 'רענון הנתונים מ-Arbox נכשל');
        else autoFailedAtRef.current = Date.now();
      })
      .finally(() => {
        if (refreshingRef.current === key) refreshingRef.current = null;
        setRefreshingKey((k) => (k === key ? null : k));
        setProgress((prev) => (prev?.key === key ? null : prev));
      });
  };
  const refreshing = refreshingKey === reportKey;
  const fetchedAt = reportQuery.data?.data_fetched_at ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const t = setInterval(tick, 30_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);
  const dataAgeMs = fetchedAt ? Math.max(0, now - Date.parse(fetchedAt)) : null;
  useEffect(() => {
    if (dataAgeMs == null || dataAgeMs < AUTO_REFRESH_MS) return;
    if (document.visibilityState !== 'visible' || reportQuery.isFetching || refreshingRef.current) return;
    // A failed background pull (e.g. Arbox busy) waits a full interval, not one tick.
    if (now - autoFailedAtRef.current < AUTO_REFRESH_MS) return;
    refreshReport('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAgeMs, reportKey, reportQuery.isFetching]);
  const freshnessLabel = refreshing
    ? throughLabel
      ? `מתעדכן מ-Arbox… נטענו עד ${throughLabel}`
      : 'מתעדכן מ-Arbox…'
    : dataAgeMs == null
      ? null
      : dataAgeMs < 60_000
        ? 'עודכן עכשיו'
        : dataAgeMs < 3_600_000
          ? `עודכן לפני ${Math.floor(dataAgeMs / 60_000)} דק׳`
          : `עודכן לפני ${Math.floor(dataAgeMs / 3_600_000)} שע׳`;
  // Private operator notes — fetched apart from the report so they can never
  // ride along into an export, a snapshot or anything the coach receives.
  const notesQuery = useQuery({
    queryKey: ['payroll-internal-notes', data?.period.start_date, data?.period.end_date],
    queryFn: () => getInternalNotes(data!.period.start_date, data!.period.end_date),
    enabled: !!data?.period.start_date,
  });
  const internalNotes = notesQuery.data ?? {};
  // A query held back by `enabled` reports isLoading:false, so keep the spinner
  // up while the default month is still being resolved.
  const reportLoading = !monthSettled || (reportQuery.isLoading && !isPartial);
  // The box's ENABLED mechanisms — a mixed box's report merges rows from all
  // of them into one table, so the column SET must be their union (each row
  // already carries 0-defaulted fields for whichever bucket doesn't apply to
  // it), not one mechanism's exclusive list. `data.box_type` is absent when
  // the report actually merged 2+ mechanisms across staff (see
  // buildConsolidatedPayrollReport), but the box's OWN settings always know
  // every mechanism it has turned on regardless of what any single period's
  // rows happen to contain.
  const boxMechanisms = auth.settings ? enabledMechanisms(auth.settings) : (['group_classes'] as BoxMechanism[]);
  const isGroupClasses = boxMechanisms.includes('group_classes');
  const isHugim = boxMechanisms.includes('hugim');
  const isTimeClock = boxMechanisms.includes('time_clock');
  // Only coaches (Arbox role COACH = מאמן/מדריך/מטפל) are paid here — EXCEPT
  // when the box has time_clock enabled at all, since its regular employees
  // almost never carry that role (and a mixed box can't reliably tell a
  // time_clock row from a group_classes/hugim one by role alone — skipping
  // the filter entirely is the safe failure mode: an extra ₪0 row shown,
  // never a real paid row hidden).
  // A שכר גלובלי row is always shown — those staff are usually NOT coaches
  // (marketing, office…), and the operator turned them on explicitly.
  const lineRows = isTimeClock
    ? (data?.rows ?? [])
    : (data?.rows ?? []).filter((r) => r.global_salary || parseRoles(r.user_role).includes('COACH'));
  // The base_salary column carries a global salary — a hugim-only box has no
  // such column otherwise, so add it whenever a global row is present.
  const hasGlobalRows = lineRows.some((r) => r.global_salary);
  // Branches to pick from, grouped by the P&L unit they belong to. Taken from
  // the rows, so a unit-scoped view only offers that unit's branches.
  const branchGroups = (() => {
    const unitOf = new Map<string, string>();
    for (const r of lineRows) {
      for (const b of r.by_location ?? []) {
        if (!unitOf.has(b.location)) unitOf.set(b.location, r.pnl_unit_name ?? '');
      }
    }
    const groups = new Map<string, string[]>();
    for (const b of branchesOf(lineRows)) {
      const u = unitOf.get(b) ?? '';
      groups.set(u, [...(groups.get(u) ?? []), b]);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, 'he'));
  })();
  const branchLabel = branchFilter === NO_BRANCH ? NO_BRANCH_LABEL : branchFilter;
  // With a branch picked, every row becomes its share of that branch (rows with
  // nothing there drop out). Lines that carry no split can't be shown by branch
  // — they're counted so the screen can say so instead of hiding them quietly.
  const allRows =
    branchFilter == null
      ? lineRows
      : lineRows.flatMap((r) => {
          const v = rowForBranch(r, branchFilter);
          return v ? [v] : [];
        });
  const unsplitCount =
    branchFilter == null ? 0 : lineRows.filter((r) => !r.is_zero && !hasBranchBreakdown(r)).length;
  const rows = showZero ? allRows : allRows.filter((r) => !r.is_zero);
  // Coaches Arbox pays for classes this period that the payroll roster has no
  // row for — their money is in NO row of this table, so it has to be said out
  // loud (a box otherwise closes and pays a period that is quietly short a
  // whole coach). Only the live report can know this; a closed period reports
  // none, because its rows come from frozen snapshots.
  const unmatchedStaff = data?.unmatched?.staff ?? [];
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const unmatchedPay = unmatchedStaff.reduce((a, u) => a + u.class_pay, 0);
  const periodStart = data?.period.start_date ?? '';
  const periodEnd = data?.period.end_date ?? '';
  const periodLabel = data?.period.label ?? '';
  // Per (coach, unit) row key. When the box doesn't split by unit, pnl_unit_id is
  // 0 and this is effectively the coach id.
  const rowKeyOf = (r: PayrollReportRow) => `${r.arbox_users_boxes_id}:${r.pnl_unit_id ?? 0}`;
  // Every P&L unit's own run status — present whenever the box splits by unit,
  // on both the combined view and a single-unit view. Drives the unit picker
  // + the per-row lock state in the combined view.
  const unitRuns: UnitRunMeta[] = data?.unit_runs ?? [];
  const hasUnits = unitRuns.length > 0;
  const selectedRows = rows.filter((r) => selectedKeys.includes(rowKeyOf(r)));
  // Whether THIS row's own unit is currently locked. In a unit-scoped view this
  // is just the page-level state (every row belongs to the selected unit); in
  // the combined view a box can be 'mixed' — some units closed, others not —
  // so each row must be checked against its own unit.
  const rowLocked = (r: PayrollReportRow): boolean =>
    isPartial ||
    (unitFilter != null || !hasUnits
      ? data?.run.status === 'locked'
      : unitRuns.find((u) => u.pnl_unit_id === (r.pnl_unit_id ?? PNL_UNIT_NONE))?.status === 'locked');
  // Page-level lock state for the CURRENTLY SELECTED scope (one unit, or the
  // combined view — which can be 'open' | 'locked' | 'mixed').
  const scopeStatus = data?.run.status ?? 'open';
  const locked = scopeStatus === 'locked';

  // Reset the captured filter set whenever the underlying data changes (new
  // month or zero-pay toggle) — antd doesn't re-fire onChange on a dataSource
  // swap, so without this the summary bar would keep stale filtered totals.
  useEffect(() => {
    setFilteredRows(null);
  }, [data, showZero, branchFilter]);

  // Summary-bar totals: reflect the rows CURRENTLY shown (post column-filter),
  // across the whole filtered set — not just the current page.
  const totalsRows = filteredRows ?? rows;
  const totalStaff = totalsRows.length;
  const totalVat = totalsRows.reduce((a, r) => a + r.vat, 0);
  const totalPay = totalsRows.reduce((a, r) => a + r.total, 0);
  // ניכוי מס במקור: only surfaced when this period actually withholds anything,
  // so boxes that don't use it never see the extra columns or summary figures.
  const totalWithholding = totalsRows.reduce((a, r) => a + (r.withholding ?? 0), 0);
  const totalNet = totalsRows.reduce((a, r) => a + (r.net_total ?? r.total), 0);
  const anyWithholding = allRows.some((r) => (r.withholding ?? 0) > 0);
  // Same rule for the one-time deduction: most boxes never take money off a
  // coach, and they shouldn't have to look at (or scroll past) an all-zero
  // column. The moment ANY row in the period carries one, the column shows by
  // default — and the summary bar picks it up too, so the run's bottom line is
  // never quietly short by an amount nobody can see.
  const totalDeduction = totalsRows.reduce((a, r) => a + (r.deduction ?? 0), 0);
  const anyDeduction = allRows.some((r) => (r.deduction ?? 0) > 0);
  // The export says which branch it covers, in its title and file name.
  const exportLabel = branchLabel ? `${periodLabel} — ${branchLabel}` : periodLabel;

  // ── Run actions: lock / reopen / finalize ───────────────────────────────
  // Closing is a full Arbox rebuild — minutes on a big box. It runs server-side
  // in the queue: we say it started, the operator carries on, and a toast lands
  // when it's done. Nothing here blocks the screen.
  // Closing is a full Arbox rebuild — minutes on a big box. It runs server-side
  // in the queue, so the mutation itself only QUEUES it and settles instantly:
  // the button must not sit spinning for minutes. The operator is told it
  // started and gets a toast when it lands; meanwhile the screen stays usable.
  // The unit currently in scope's display name, for the confirm-dialog copy —
  // null in the combined view (or a box that doesn't split).
  const scopedUnitName = unitFilter != null
    ? unitRuns.find((u) => u.pnl_unit_id === unitFilter)?.pnl_unit_name ?? null
    : null;

  const watchLock = () => {
    toast('סוגרים את התקופה — זה עשוי לקחת כמה דקות. נעדכן כשיהיה מוכן.', {
      icon: '⏳',
      duration: 7000,
    });
    void awaitRunJob(selection, 'lock_run', { unitId: unitFilter })
      .then(() => {
        toast.success('התקופה נסגרה');
        void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
        void queryClient.invalidateQueries({ queryKey: ['closed-runs'] });
        // Closing a period changes which month the report should open on.
        void queryClient.invalidateQueries({ queryKey: ['payroll-default-period'] });
        // Jump straight into the frozen report, where the success popup opens
        // — only when the WHOLE period (this scope) is now closed; a single
        // unit closing out of several still-open ones stays on this screen.
        if (!hasUnits || unitFilter != null) {
          onPeriodClosed?.({
            period_start: periodStart,
            period_end: periodEnd,
            period_label: data?.period.label ?? '',
            pnl_unit_id: unitFilter ?? PNL_UNIT_NONE,
            pnl_unit_name: scopedUnitName,
          });
        }
      })
      .catch((e: unknown) => toast.error((e as Error).message || 'שגיאה בסגירת התקופה'));
  };
  const lockMutation = useMutation({
    mutationFn: () => lockRun(selection, unitFilter),
    onSuccess: watchLock,
    onError: (e) => toast.error((e as Error).message || 'שגיאה בסגירת התקופה'),
  });
  const reopenMutation = useMutation({
    mutationFn: () => reopenRun(selection, unitFilter),
    onSuccess: () => {
      toast.success('התקופה נפתחה מחדש — הדוח חי');
      void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
      void queryClient.invalidateQueries({ queryKey: ['closed-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-default-period'] });
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה בפתיחת התקופה'),
  });
  const finalizeMutation = useMutation({
    mutationFn: ({ ids, unitId }: { ids: number[]; unitId?: number }) =>
      finalizeLines(selection, ids, unitId),
    onSuccess: (r) => {
      toast.success(`${r.finalized} שורות סומנו כסופי`);
      void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה בסימון'),
  });

  // "חישוב מחדש" — a full Arbox rebuild diffed against the frozen snapshots.
  // Queue it, say so, and open the diff modal only when the answer is ready.
  // Deliberately does NOT open a modal with a spinner in it.
  const startRecompute = async () => {
    try {
      await recomputePreview(selection, undefined, unitFilter);
      toast('מחשבים מחדש מול Arbox — זה עשוי לקחת כמה דקות. נעדכן כשיהיה מוכן.', {
        icon: '⏳',
        duration: 7000,
      });
      const diff = await awaitRunJob<RecomputeDiff>(selection, 'recompute_preview', { unitId: unitFilter });
      setRecomputeUnitId(unitFilter);
      setRecomputeDiff(diff);
      setRecomputeOpen(true);
    } catch (e) {
      toast.error((e as Error).message || 'שגיאה בחישוב מחדש');
    }
  };

  const confirmLock = () =>
    confirmModal({
      title: scopedUnitName ? `סגירת התקופה — ${scopedUnitName}` : 'סגירת התקופה',
      content: scopedUnitName
        ? `ייווצר דוח סופי קפוא לכל אנשי הצוות של ${scopedUnitName} בתקופה זו. יחידות רווח אחרות לא יושפעו. מרגע זה הדוח לא יתעדכן אוטומטית מ-Arbox; לתיקונים יש להשתמש ב"חישוב מחדש". להמשיך?`
        : 'ייווצר דוח סופי קפוא לכל אנשי הצוות בתקופה. מרגע זה הדוח לא יתעדכן אוטומטית מ-Arbox; לתיקונים יש להשתמש ב"חישוב מחדש". להמשיך?',
      okText: 'סגור תקופה',
      cancelText: 'ביטול',
      onOk: () => lockMutation.mutateAsync(),
    });
  const confirmReopen = () =>
    confirmModal({
      title: scopedUnitName ? `פתיחת התקופה מחדש — ${scopedUnitName}` : 'פתיחת התקופה מחדש',
      content: 'הדוח יחזור להתעדכן חי מ-Arbox. הנתונים הקפואים יישמרו עד סגירה הבאה. להמשיך?',
      okText: 'פתח מחדש',
      cancelText: 'ביטול',
      onOk: () => reopenMutation.mutateAsync(),
    });

  // Bulk send-for-approval over the selected rows. Sends sequentially; the
  // server skips zero-pay / no-email staff, so we surface a per-batch summary.
  const confirmBulkSend = () => {
    const targets = selectedRows;
    if (targets.length === 0) return;
    // Count DISTINCT coaches (a coach split across units is one recipient).
    const total = new Set(targets.map((t) => t.arbox_users_boxes_id)).size;
    confirmModal({
      title: 'שליחת שכר לאישור',
      content: locked
        ? `לשלוח את פירוט השכר ל-${total} אנשי צוות שנבחרו?`
        : `לשלוח את פירוט השכר ל-${total} אנשי צוות שנבחרו? התקופה תיסגר לפני השליחה.`,
      okText: 'שלח',
      cancelText: 'ביטול',
      onOk: async () => {
        // Fire and forget. The server closes the period if needed and sends every
        // coach in a durable background queue, so there is nothing here worth
        // waiting on: no loader, no progress poll, no reason to keep the tab open.
        // Sending a coach emails ALL their P&L units (separate mail each), so the
        // batch is keyed by DISTINCT coach — selecting two unit rows of one coach
        // still sends them once.
        await sendBatch(
          Array.from(new Set(targets.map((t) => t.arbox_users_boxes_id))),
          selection,
        );
        setSelectedKeys([]);
        toast.success(
          `השליחה החלה. המיילים נשלחים ל-${total} אנשי צוות ברקע — אפשר לסגור את המסך.`,
          { duration: 6000 },
        );
        // Rows flip to "נשלח" on their own as the queue works through them.
        void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
        window.setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: ['payroll-report'] }),
          20_000,
        );
      },
    });
  };

  // Single send-for-approval. Queues server-side (returns immediately — no
  // blocking on the PDF render) then polls that coach's outcome for the toast.
  const sendSingle = (row: PayrollReportRow) => {
    // Instant guards for the common cases (the queue would otherwise skip silently).
    if (!row.email) {
      toast.error('לאיש הצוות אין כתובת אימייל');
      return;
    }
    if (row.total < 0) {
      // Manual offsets larger than the month's pay — nothing to invoice.
      toast.error('סה"כ השכר בחודש זה שלילי, ולכן אין מה לשלוח לאישור');
      return;
    }
    if (row.total === 0) {
      toast.error('אין שכר לשליחה (שכר אפס)');
      return;
    }
    confirmModal({
      title: 'שליחת שכר לאישור',
      // With P&L units the row IS the thing being sent — name the branch, so it
      // is obvious this mail covers one unit and not the coach's whole month.
      content: row.pnl_unit_name
        ? `לשלוח את פירוט השכר של ${row.pnl_unit_name} ל${row.display_name} (${row.email})?`
        : `לשלוח את פירוט השכר ל${row.display_name} (${row.email})?`,
      okText: 'שלח',
      cancelText: 'ביטול',
      onOk: async () => {
        // Enqueue this ROW's unit only (returns at once).
        await sendPayroll(row.arbox_users_boxes_id, selection, row.pnl_unit_id);
        const toastId = toast.loading('שולח שכר…');
        const startedAt = Date.now();
        const poll = async () => {
          if (Date.now() - startedAt > 5 * 60_000) {
            toast.dismiss(toastId);
            return;
          }
          try {
            const outcome = await getSingleSendStatus(
              row.arbox_users_boxes_id,
              selection,
              row.pnl_unit_id,
            );
            if (outcome === 'pending' || outcome === 'none') {
              window.setTimeout(poll, 2000);
              return;
            }
            void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
            if (outcome === 'sent') toast.success(`נשלח ל${row.email}`, { id: toastId });
            else if (outcome === 'skipped') toast.error('דולג (שכר אפס או ללא אימייל)', { id: toastId });
            else toast.error('השליחה נכשלה', { id: toastId });
          } catch {
            window.setTimeout(poll, 3000); // transient — keep trying
          }
        };
        window.setTimeout(poll, 1200);
      },
    });
  };

  // Operator revert: cancel a sent/approved line back to draft (kills the old
  // link + clears the invoice), so it can be edited and re-sent.
  const revertSingle = (row: PayrollReportRow) =>
    confirmModal({
      title: 'החזרת שכר לטיוטה',
      content: `הפעולה תבטל את השליחה/אישור של ${row.display_name}, תנטרל את קישור האישור הקודם, ותמחק חשבונית שצורפה. לאחר מכן ניתן לערוך ולשלוח מחדש. להמשיך?`,
      okText: 'החזר לטיוטה',
      cancelText: 'ביטול',
      onOk: () =>
        toast
          .promise(revertLine(row.arbox_users_boxes_id, selection, row.pnl_unit_id), {
            loading: 'מחזיר לטיוטה…',
            success: 'השכר הוחזר לטיוטה',
            error: (e) => (e as Error).message || 'שגיאה בהחזרה לטיוטה',
          })
          .then(() => void queryClient.invalidateQueries({ queryKey: ['payroll-report'] })),
    });

  // Operator: clear just the invoice (leaves the status as-is).
  const clearInvoiceSingle = (row: PayrollReportRow) =>
    confirmModal({
      title: 'מחיקת חשבונית',
      content: `למחוק את החשבונית שצורפה ל${row.display_name}? איש הצוות יוכל להעלות חשבונית חדשה דרך קישור האישור הקיים.`,
      okText: 'מחק חשבונית',
      cancelText: 'ביטול',
      onOk: () =>
        toast
          .promise(clearLineInvoice(row.arbox_users_boxes_id, selection, row.pnl_unit_id), {
            loading: 'מוחק חשבונית…',
            success: 'החשבונית נמחקה',
            error: (e) => (e as Error).message || 'שגיאה במחיקת החשבונית',
          })
          .then(() => void queryClient.invalidateQueries({ queryKey: ['payroll-report'] })),
    });

  const rowActions = (row: PayrollReportRow) => {
    // Revert applies once a line left 'draft' and isn't finalized.
    const canRevert =
      row.status === 'sent' || row.status === 'approved' || row.status === 'needs_correction';
    const canClearInvoice = row.receipt_uploaded && row.status !== 'final';
    // While the report is still partial only the breakdown opens (it waits for
    // the full data on its own); nothing that sends, saves or exports.
    if (isPartial) {
      return {
        items: [{ key: 'detail', icon: <FontAwesomeIcon icon={faListUl} />, label: 'הצג פירוט' }],
        onClick: () =>
          setDetailStaff({
            id: row.arbox_users_boxes_id,
            name: row.display_name,
            unitId: row.pnl_unit_id,
            unitName: row.pnl_unit_name,
            invoiceCheckStatus: row.invoice_check_status,
            invoiceAmount: row.invoice_amount,
            invoiceCheckIssues: row.invoice_check_issues,
          }),
      };
    }
    return {
    items: [
      { key: 'detail', icon: <FontAwesomeIcon icon={faListUl} />, label: 'הצג פירוט' },
      { key: 'salary-settings', icon: <FontAwesomeIcon icon={faSlidersH} />, label: 'הגדרות שכר' },
      ...(canSend
        ? [{ key: 'send', icon: <FontAwesomeIcon icon={faPaperPlane} />, label: 'שלח שכר לאישור' }]
        : []),
      ...(canRevert
        ? [{ key: 'revert', icon: <FontAwesomeIcon icon={faRotateLeft} />, label: 'החזר לטיוטה' }]
        : []),
      ...(canClearInvoice
        ? [{ key: 'clear-invoice', icon: <FontAwesomeIcon icon={faTrashCan} />, label: 'מחק חשבונית' }]
        : []),
      ...(rowLocked(row)
        ? [{ key: 'finalize', icon: <FontAwesomeIcon icon={faCircleCheck} />, label: 'סמן כסופי' }]
        : []),
      { key: 'excel', icon: <FontAwesomeIcon icon={faFileExcel} />, label: 'ייצוא לאקסל' },
      { key: 'print', icon: <FontAwesomeIcon icon={faPrint} />, label: 'הדפסה' },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'detail') {
        setDetailStaff({
          id: row.arbox_users_boxes_id,
          name: row.display_name,
          unitId: row.pnl_unit_id,
          unitName: row.pnl_unit_name,
          invoiceCheckStatus: row.invoice_check_status,
          invoiceAmount: row.invoice_amount,
          invoiceCheckIssues: row.invoice_check_issues,
        });
      } else if (key === 'salary-settings') {
        setSalaryEditStaff({ id: row.arbox_users_boxes_id, name: row.display_name });
      } else if (key === 'finalize') {
        finalizeMutation.mutate({ ids: [row.arbox_users_boxes_id], unitId: row.pnl_unit_id });
      } else if (key === 'send') {
        sendSingle(row);
      } else if (key === 'revert') {
        revertSingle(row);
      } else if (key === 'clear-invoice') {
        clearInvoiceSingle(row);
      } else if (key === 'excel') {
        toast.promise(downloadPayrollExcel(row.arbox_users_boxes_id, selection, row.pnl_unit_id), {
          loading: 'מפיק קובץ…',
          success: 'הקובץ ירד',
          error: (e) => (e as Error).message || 'שגיאה בהפקת הקובץ',
        });
      } else exportPayrollPrint([row], periodLabel, boxMechanisms);
    },
    };
  };

  // ── Column sort kit (mirrors ArboxExpenses) ─────────────────────────────
  // The calculation columns are SORT-ONLY (no per-column filter) — first click
  // sorts large→small. Filters live only on שם / סטטוס / חשבונית. Sorting is
  // client-side (the report loads all rows).
  const numCol = (key: keyof PayrollReportRow) => ({
    sorter: (a: PayrollReportRow, b: PayrollReportRow) => (Number(a[key]) || 0) - (Number(b[key]) || 0),
    sortDirections: ['descend', 'ascend'] as ('descend' | 'ascend')[],
  });

  // Factory → an antd filterDropdown render-prop that mounts the component above
  // (so its hooks live in their own instance, not in this render).
  const multiSelectFilterDropdown =
    (options: { text: string; value: string }[], withSearch = false) =>
    ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <MultiSelectFilterDropdown
        options={options}
        withSearch={withSearch}
        setSelectedKeys={setSelectedKeys}
        selectedKeys={selectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
      />
    );

  const nameOptions = Array.from(new Set(allRows.map((r) => r.display_name)))
    .sort((a, b) => a.localeCompare(b, 'he'))
    .map((n) => ({ text: n, value: n }));
  const statusOptions = [
    { text: 'צריך תיקונים', value: 'needs_correction' },
    { text: 'טרם נשלח', value: 'draft' },
    { text: 'נשלח', value: 'sent' },
    { text: 'אושר', value: 'approved' },
  ];
  const columns: ColumnsType<PayrollReportRow> = [
    {
      title: 'שם',
      dataIndex: 'display_name',
      fixed: 'right',
      width: 140,
      sorter: (a, b) => a.display_name.localeCompare(b.display_name, 'he'),
      filterDropdown: multiSelectFilterDropdown(nameOptions, true),
      onFilter: (value, r) => r.display_name === value,
      render: (name: string, row) => (
        <button
          type="button"
          onClick={() => openArboxProfile(row.arbox_users_boxes_id)}
          className="text-primary hover:underline cursor-pointer text-start"
          title="פתח פרופיל בארבוקס"
        >
          {name}
          {row.global_salary && (
            <Tooltip title="שכר גלובלי: סכום קבוע בכל תקופה, מוצג בעמודת שכר בסיס">
              <span className="inline-block ms-1.5 rounded px-1.5 text-small font-medium text-secondary-text bg-grey-bg">
                גלובלי
              </span>
            </Tooltip>
          )}
        </button>
      ),
    },
    // P&L unit — only when the box splits payroll by unit this period.
    ...(hasUnits
      ? ([
          {
            title: 'יחידת רווח והפסד',
            dataIndex: 'pnl_unit_name',
            width: 150,
            sorter: (a: PayrollReportRow, b: PayrollReportRow) =>
              (a.pnl_unit_name ?? '').localeCompare(b.pnl_unit_name ?? '', 'he'),
            filterDropdown: multiSelectFilterDropdown(
              Array.from(new Set(allRows.map((r) => r.pnl_unit_name).filter(Boolean) as string[]))
                .sort((a, b) => a.localeCompare(b, 'he'))
                .map((n) => ({ text: n, value: n })),
            ),
            onFilter: (value: boolean | React.Key, r: PayrollReportRow) =>
              (r.pnl_unit_name ?? '') === value,
            render: (n: string | null | undefined) =>
              n ? <span>{n}</span> : <span className="text-secondary-text">—</span>,
          },
        ] as ColumnsType<PayrollReportRow>)
      : []),
    // Method-specific activity/earning columns — a UNION of whichever
    // mechanisms this box has enabled (never mutually exclusive), since a
    // mixed box's report merges rows from all of them into one table. Every
    // row already carries 0-defaulted fields for whichever bucket doesn't
    // apply to it (see PayrollReportRow), so no per-row branching is needed
    // here — only which COLUMNS exist at all.
    ...(isHugim
      ? ([
          {
            title: 'שכר חוגים',
            dataIndex: 'hug_pay',
            width: 110,
            ...numCol('hug_pay'),
            // hug_pay includes the add-ons and the manual additions/offsets;
            // shown without them, since each has its own column, so the row's
            // columns still add up.
            render: (_: number, r: PayrollReportRow) => <Money value={hugCoursePay(r)} />,
          },
          // מוצרים נלווים — only once some coach this month is paid on them.
          ...(allRows.some((r) => (r.addons_pay ?? 0) !== 0)
            ? [
                {
                  title: 'תשלום על נלווים',
                  dataIndex: 'addons_pay',
                  width: 120,
                  ...numCol('addons_pay'),
                  render: (v: number | undefined) =>
                    v ? <Money value={v} /> : <span className="text-secondary-text">—</span>,
                },
              ]
            : []),
          // תוספות וקיזוזים ידניים — from the staff member's settings, kept apart
          // from the report's own one-time bonus. Always present (0 on a box
          // that doesn't use them), like any other pay component.
          {
            title: 'תוספות',
            dataIndex: 'manual_additions',
            width: 100,
            ...numCol('manual_additions'),
            render: (v: number | undefined) => <Money value={v ?? 0} />,
          },
          {
            title: 'קיזוזים',
            dataIndex: 'manual_offsets',
            width: 100,
            ...numCol('manual_offsets'),
            // Stored positive, shown like the deduction column: red with a minus.
            render: (v: number | undefined) =>
              v ? (
                <span className="text-error-red">
                  -<Money value={v} />
                </span>
              ) : (
                <Money value={0} />
              ),
          },
          { title: 'משתתפים', dataIndex: 'participant_count', width: 90, ...numCol('participant_count') },
        ] as ColumnsType<PayrollReportRow>)
      : []),
    ...(isGroupClasses
      ? ([
          { title: 'רשומים', dataIndex: 'registration_count', width: 80, ...numCol('registration_count') },
          { title: "צ'ק-אין", dataIndex: 'checkin_count', width: 80, ...numCol('checkin_count') },
          { title: 'ביטולים', dataIndex: 'late_cancel_count', width: 80, ...numCol('late_cancel_count') },
          { title: 'סך שיעורים', dataIndex: 'class_count', width: 90, ...numCol('class_count') },
          { title: 'שכר שיעורים', dataIndex: 'class_pay', width: 110, ...numCol('class_pay'), render: (v: number) => <Money value={v} /> },
          { title: 'תוספת סופ"ש', dataIndex: 'weekend_bonus', width: 110, ...numCol('weekend_bonus'), render: (v: number) => <Money value={v} /> },
          { title: 'תוספת שיעור', dataIndex: 'class_bonus', width: 110, ...numCol('class_bonus'), render: (v: number) => <Money value={v} /> },
          { title: 'בונוס כמות שיעורים', dataIndex: 'volume_bonus', width: 130, ...numCol('volume_bonus'), render: (v: number) => <Money value={v ?? 0} /> },
          { title: 'בונוס המרה', dataIndex: 'conversion_bonus', width: 110, ...numCol('conversion_bonus'), render: (v: number) => <Money value={v} /> },
          { title: 'שיעורים ריקים', dataIndex: 'empty_class_pay', width: 110, ...numCol('empty_class_pay'), render: (v: number) => <Money value={v} /> },
          { title: 'שיעורים שבוטלו', dataIndex: 'cancelled_class_pay', width: 120, ...numCol('cancelled_class_pay'), render: (v: number) => <Money value={v ?? 0} /> },
        ] as ColumnsType<PayrollReportRow>)
      : []),
    // Shared between group_classes and time_clock (same dataIndex, same
    // meaning either way — hugim's rows just carry 0 here) — included once
    // whichever of those two mechanisms is enabled, never duplicated when
    // both are.
    ...(isGroupClasses || isTimeClock
      ? ([
          {
            // A pure time_clock box means this IS the salary-relevant
            // attendance-days count; a group_classes box (mixed or not) uses
            // the more familiar generic label.
            title: isGroupClasses ? 'סך ימים' : 'ימי נוכחות',
            dataIndex: 'workdays_count',
            width: 90,
            ...numCol('workdays_count'),
          },
          { title: 'שכר בסיס', dataIndex: 'base_salary', width: 100, ...numCol('base_salary'), render: (v: number) => <Money value={v} /> },
          { title: 'נסיעות', dataIndex: 'travel', width: 90, ...numCol('travel'), render: (v: number) => <Money value={v} /> },
        ] as ColumnsType<PayrollReportRow>)
      : hasGlobalRows
        ? ([
            { title: 'שכר בסיס', dataIndex: 'base_salary', width: 100, ...numCol('base_salary'), render: (v: number) => <Money value={v} /> },
          ] as ColumnsType<PayrollReportRow>)
        : []),
    // time_clock only — flags a staff member with an unclosed clock-in this
    // period (see AttendanceByStaff): the day still counts toward workdays/
    // travel, but its hours were dropped, so an hourly base salary is likely
    // missing pay for it. Worth surfacing at a glance, not just in the detail.
    ...(isTimeClock
      ? ([
          {
            title: 'שעות חסרות',
            dataIndex: 'incomplete_attendance_days_count',
            width: 100,
            ...numCol('incomplete_attendance_days_count'),
            render: (v: number | undefined) =>
              v ? (
                <Tooltip title="ישנם ימים עם שעת יציאה חסרה בדוח הנוכחות — יש לבדוק בפירוט השכר">
                  <span className="text-warning-yellow font-medium">
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginInlineEnd: 4 }} />
                    {v}
                  </span>
                </Tooltip>
              ) : (
                <span className="text-secondary-text">—</span>
              ),
          },
        ] as ColumnsType<PayrollReportRow>)
      : []),
    // The one-time bonus and the one-time deduction are method-agnostic — a
    // חוגים coach can be granted one, or fined one, on top of their
    // %-of-revenue pay exactly like a group-classes coach — so these columns
    // sit outside the method-specific blocks.
    {
      title: 'בונוס חד-פעמי',
      dataIndex: 'one_time_bonus',
      width: 140,
      ...numCol('one_time_bonus'),
      render: (_: unknown, row: PayrollReportRow) => (
        // Keyed on the stored values: after a save + refetch the cell
        // remounts on the server's answer, so what's on screen is always
        // what's actually stored — never leftover local edit state.
        <AdjustmentCell
          key={`bonus:${row.arbox_users_boxes_id}:${row.pnl_unit_id ?? 0}:${row.one_time_bonus_mode}:${row.one_time_bonus_amount}:${row.one_time_bonus_percent}:${row.one_time_bonus_note ?? ''}`}
          row={row}
          kind="bonus"
          periodStart={periodStart}
          periodEnd={periodEnd}
          // A branch view shows a share of the line — editing the line's bonus
          // from it would be confusing, so it's edited on the whole line only.
          locked={rowLocked(row) || branchFilter != null}
        />
      ),
    },
    {
      title: 'הפחתה',
      dataIndex: 'deduction',
      width: 140,
      // Sorted/filtered on the stored positive figure; only the cell renders it
      // with its minus sign.
      ...numCol('deduction'),
      render: (_: unknown, row: PayrollReportRow) => (
        <AdjustmentCell
          key={`deduction:${row.arbox_users_boxes_id}:${row.pnl_unit_id ?? 0}:${row.deduction_mode}:${row.deduction_amount}:${row.deduction_percent}:${row.deduction_note ?? ''}`}
          row={row}
          kind="deduction"
          periodStart={periodStart}
          periodEnd={periodEnd}
          locked={rowLocked(row) || branchFilter != null}
        />
      ),
    },
    {
      title: 'סוג',
      dataIndex: 'tax_type',
      width: 90,
      render: (t: keyof typeof TAX_TYPE_LABELS) => (
        <span className="text-small text-main-text">{TAX_TYPE_LABELS[t] ?? t}</span>
      ),
    },
    {
      title: 'מע"מ',
      dataIndex: 'vat',
      width: 90,
      ...numCol('vat'),
      render: (v: number) => (v > 0 ? <Money value={v} /> : <span className="text-secondary-text">—</span>),
    },
    {
      title: 'ניכוי במקור',
      dataIndex: 'withholding',
      width: 100,
      ...numCol('withholding'),
      render: (v: number, row) =>
        v > 0 ? (
          <span className="text-error-red" title={`${formatWithholdingPct(row.withholding_pct)} מסה"כ`}>
            -<Money value={v} />
          </span>
        ) : (
          <span className="text-secondary-text">—</span>
        ),
    },
    {
      title: 'סה"כ',
      dataIndex: 'total',
      fixed: 'right',
      width: 110,
      ...numCol('total'),
      render: (v) => (
        <span className="font-semibold">
          <Money value={v} />
        </span>
      ),
    },
    {
      title: 'להעברה',
      dataIndex: 'net_total',
      fixed: 'right',
      width: 110,
      ...numCol('net_total'),
      render: (v: number, row) => (
        <span className={row.withholding > 0 ? 'font-semibold' : 'text-secondary-text'}>
          <Money value={v} />
        </span>
      ),
    },
    {
      title: 'סטטוס',
      dataIndex: 'status',
      width: 90,
      sorter: (a, b) => a.status.localeCompare(b.status),
      filterDropdown: multiSelectFilterDropdown(statusOptions),
      onFilter: (value, r) => r.status === value,
      render: (s: PayrollLineStatus, row) => <StatusLabel status={s} comment={row.correction_comment} />,
    },
    {
      title: 'חשבונית',
      dataIndex: 'receipt_uploaded',
      width: 70,
      // Filtering by the check verdict is the point of the column for a box that
      // verifies amounts — "show me the ones that don't add up" is a one-click
      // question, not a scroll through every row.
      filterDropdown: multiSelectFilterDropdown([
        { text: 'הועלתה', value: 'true' },
        { text: 'ללא', value: 'false' },
        { text: INVOICE_CHECK_LABELS.mismatch, value: 'mismatch' },
        { text: INVOICE_CHECK_LABELS.unreadable, value: 'unreadable' },
      ]),
      onFilter: (value, r) =>
        value === 'mismatch' || value === 'unreadable'
          ? r.invoice_check_status === value
          : String(r.receipt_uploaded) === value,
      render: (uploaded: boolean, row) =>
        uploaded ? (
          <ReceiptPreview
            staffId={row.arbox_users_boxes_id}
            unitId={row.pnl_unit_id}
            selection={selection}
            invoiceUrl={row.invoice_url}
            checkStatus={row.invoice_check_status}
            checkIssues={row.invoice_check_issues}
          />
        ) : (
          <span className="text-secondary-text">—</span>
        ),
    },
    {
      title: 'הערה פנימית',
      key: 'internal_note',
      width: 160,
      render: (_: unknown, row: PayrollReportRow) => {
        const note = internalNotes[rowKeyOf(row)] ?? null;
        return (
          <InternalNoteCell
            key={`${rowKeyOf(row)}:${note ?? ''}`}
            row={row}
            note={note}
            periodStart={periodStart}
            periodEnd={periodEnd}
          />
        );
      },
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 48,
      render: (_, row) => (
        <Dropdown menu={rowActions(row)} trigger={['click']} placement="bottomLeft">
          <button
            type="button"
            aria-label="פעולות"
            className="p-2 rounded text-secondary-text hover:bg-grey-bg hover:text-main-text"
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
        </Dropdown>
      ),
    },
  ];

  // Column management — name + actions are structural (always shown); the rest
  // are togglable via the picker and persisted (mirrors expenses' columnsState).
  const ALWAYS = ['display_name', 'actions'];
  // The Arbox activity-count columns + roles are hidden BY DEFAULT (still
  // available in the picker). Salary columns stay visible by default. For a
  // time_clock box, workdays_count IS the salary-relevant column (attendance
  // days), not an auxiliary activity count — keep it visible there.
  const DEFAULT_HIDDEN = new Set([
    'user_role',
    'registration_count',
    'checkin_count',
    'late_cancel_count',
    ...(isTimeClock ? [] : ['workdays_count']),
    'class_count',
    'tax_type',
    // 'vat' stays visible by default — but it shows "—" for non-VAT staff.
    // ניכוי מס במקור is rare; both its columns stay hidden unless this period
    // actually withholds something (see anyWithholding below).
    ...(anyWithholding ? [] : ['withholding', 'net_total']),
    // Hidden until this period actually deducts from someone — see anyDeduction.
    // Still reachable any time from the column picker, which is how an operator
    // enters the first one.
    ...(anyDeduction ? [] : ['deduction']),
  ]);
  const colId = (c: (typeof columns)[number]) =>
    String((c as { key?: string }).key ?? (c as { dataIndex?: string }).dataIndex);
  const isVisible = (id: string) => {
    if (ALWAYS.includes(id)) return true;
    const stored = columnsState[id]?.show;
    // No stored preference → default-hidden set decides; else respect the toggle.
    return stored === undefined ? !DEFAULT_HIDDEN.has(id) : stored;
  };
  const visibleColumns = columns.filter((c) => isVisible(colId(c)));
  const togglable = columns.filter((c) => !ALWAYS.includes(colId(c)));

  // Numeric total width → antd uses table-layout:fixed so the header and the
  // (separately-rendered) sticky body align exactly. +32 for the selection
  // checkbox column. Without this, scroll.x:'max-content' lets the body
  // auto-size columns to content and drift from the fixed-width header.
  const scrollX =
    32 +
    visibleColumns.reduce((sum, c) => sum + (typeof c.width === 'number' ? c.width : 100), 0);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Period + filter toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <DatePicker
          picker="month"
          allowClear={false}
          format="MMMM YYYY"
          value={dayjs(`${month.year}-${String(month.month).padStart(2, '0')}-01`)}
          // Full months only. Future months allowed so the owner can preview/
          // estimate an upcoming month's payroll.
          onChange={(d) => {
            if (d) pickMonth({ year: d.year(), month: d.month() + 1 });
          }}
        />
        {/* One click from any past month to the current one — the common move once
            last month's payroll is behind you. */}
        {monthIndex(month) < monthIndex(currentMonth) && (
          <Button type="link" className="px-0" onClick={() => pickMonth(currentMonth)}>
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginInlineEnd: 6 }} />
            עבור ל{currentMonth.label}
          </Button>
        )}
        {/* The default moved on while last month still has work on it — never let
            an unpaid period drop off the screen silently. */}
        {defaultPeriod?.last_month.unfinished &&
          monthIndex(month) >= monthIndex(currentMonth) && (
            <Button
              type="link"
              className="px-0"
              // warning-yellow; inline because antd's link Button colours itself.
              style={{ color: '#eb6e00' }}
              onClick={() => pickMonth(defaultPeriod.last_month)}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginInlineEnd: 6 }} />
              שכר {defaultPeriod.last_month.label} טרם נסגר
            </Button>
          )}
        {/* P&L-unit picker — a box that splits payroll can scope the whole
            screen (report, close/reopen, recompute) to ONE unit, so closing
            סניף תל אביב never touches הרצליה's own still-open period. */}
        {hasUnits && (
          <Select
            value={unitFilter ?? 'all'}
            style={{ minWidth: 160 }}
            onChange={(v) => {
              setUnitFilter(v === 'all' ? undefined : Number(v));
              setBranchFilter(undefined);
              setSelectedKeys([]);
            }}
            options={[
              { value: 'all', label: 'כל היחידות' },
              ...unitRuns.map((u) => ({
                value: u.pnl_unit_id,
                label: (
                  <span className="flex items-center gap-1.5">
                    {u.status === 'locked' ? (
                      <FontAwesomeIcon icon={faLock} className="text-success-green" style={{ fontSize: 11 }} />
                    ) : (
                      <FontAwesomeIcon icon={faLockOpen} className="text-secondary-text" style={{ fontSize: 11 }} />
                    )}
                    {u.pnl_unit_name}
                  </span>
                ),
              })),
            ]}
          />
        )}
        {/* Branch picker — a view inside the P&L units: shows each line's share
            of one branch. Only when the box splits by unit. */}
        {hasUnits && branchGroups.length > 0 && (
          <Select
            value={branchFilter ?? 'all'}
            style={{ minWidth: 160 }}
            onChange={(v) => setBranchFilter(v === 'all' ? undefined : v)}
            options={[
              { value: 'all', label: 'כל הסניפים' },
              ...branchGroups.map(([unit, branches]) => ({
                label: unit || 'סניפים',
                options: branches.map((b) => ({ value: b, label: b })),
              })),
              { value: NO_BRANCH, label: NO_BRANCH_LABEL },
            ]}
          />
        )}
        <label className="flex items-center gap-2 text-small text-main-text cursor-pointer">
          <Checkbox checked={showZero} onChange={(e) => setShowZero(e.target.checked)} />
          הצג גם שכר אפס
        </label>
        {scopeStatus === 'locked' && data?.run.locked_at && (
          <span className="text-small text-success-green">
            <FontAwesomeIcon icon={faLock} style={{ marginInlineEnd: 6 }} />
            תקופה סגורה ({formatDateDDMMYYYY(data.run.locked_at)})
          </span>
        )}
        {scopeStatus === 'mixed' && (
          <span className="text-small text-warning-yellow">
            <FontAwesomeIcon icon={faLock} style={{ marginInlineEnd: 6 }} />
            חלק מהיחידות סגורות
          </span>
        )}

        {/* Trailing action cluster (ms-auto, visual-left/end in RTL) — run actions
            (lockers "פעולות" placement, no icons) + the columns picker. */}
        <div className="flex items-center gap-3 ms-auto">
          {/* Only for a report built from Arbox data — a fully closed view is
              frozen snapshots and has nothing to refresh. */}
          {/* A first load still paging in from Arbox: one quiet line, where the
              "עודכן לפני…" label sits once it's complete. */}
          {isPartial && (
            <span className="flex items-center gap-2 text-small text-secondary-text">
              <Loader size={14} />
              {throughLabel ? `טוען מ-Arbox… נטענו שיעורים עד ${throughLabel}` : 'טוען מ-Arbox…'}
              <span>· הסכומים חלקיים</span>
            </span>
          )}
          {data && !isPartial && fetchedAt && (
            <>
              <span className="text-small text-secondary-text">{freshnessLabel}</span>
              <Button
                icon={<FontAwesomeIcon icon={faRotateRight} />}
                loading={refreshing}
                onClick={() => refreshReport('manual')}
              >
                רענון
              </Button>
            </>
          )}
          {data && !isPartial &&
            (locked ? (
              <>
                <Button onClick={startRecompute}>חישוב מחדש</Button>
                <Button loading={reopenMutation.isPending} onClick={confirmReopen}>
                  פתח מחדש
                </Button>
              </>
            ) : (
              <Button type="primary" loading={lockMutation.isPending} onClick={confirmLock}>
                {scopeStatus === 'mixed' ? 'סגור יחידות פתוחות' : 'סגור תקופה'}
              </Button>
            ))}
          {data && !isPartial && (
            <>
              <Tooltip title="הדפסה">
                <button
                  type="button"
                  title="הדפסה"
                  onClick={() => exportPayrollPrint(totalsRows, exportLabel, boxMechanisms)}
                  className="flex items-center justify-center w-9 h-9 bg-white rounded shadow-sm hover:bg-grey-bg transition-colors"
                >
                  <FontAwesomeIcon icon={faPrint} />
                </button>
              </Tooltip>
              {/* דוח מסכם = the table as shown (client-side CSV); דוח מפורט =
                  the same coaches, one row per class, built on the server. */}
              <Dropdown
                trigger={['click']}
                placement="bottomLeft"
                menu={{
                  items: [
                    { key: 'summary', label: 'דוח מסכם' },
                    { key: 'detailed', label: 'דוח מפורט' },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'summary') {
                      exportPayrollCsv(totalsRows, exportLabel, boxMechanisms);
                      return;
                    }
                    toast.promise(
                      downloadDetailedPayrollExcel(
                        selection,
                        totalsRows.map((r) => ({
                          staff: r.arbox_users_boxes_id,
                          unit: r.pnl_unit_id ?? PNL_UNIT_NONE,
                        })),
                      ),
                      {
                        loading: 'מפיק קובץ…',
                        success: 'הקובץ ירד',
                        error: (e) => (e as Error).message || 'שגיאה בהפקת הקובץ',
                      },
                    );
                  },
                }}
              >
                <Tooltip title="ייצוא לאקסל">
                  <button
                    type="button"
                    title="ייצוא לאקסל"
                    className="flex items-center justify-center w-9 h-9 bg-white rounded shadow-sm hover:bg-grey-bg transition-colors"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                </Tooltip>
              </Dropdown>
            </>
          )}
          <Tooltip title="ניהול עמודות">
            <Popover
              open={columnsPickerOpen}
              onOpenChange={setColumnsPickerOpen}
              trigger="click"
              placement="bottomLeft"
              overlayInnerStyle={{ padding: 4 }}
              content={
                <div className="flex flex-col py-1 min-w-[200px]">
                  <div className="px-3 pb-2 mb-1 border-b border-border text-label font-medium text-main-text">
                    עמודות
                  </div>
                  {togglable.map((c) => {
                    const id = colId(c);
                    return (
                      <label
                        key={id}
                        className="flex items-center gap-2.5 px-3 py-2 text-small text-main-text hover:bg-grey-bg cursor-pointer"
                      >
                        <Checkbox
                          checked={isVisible(id)}
                          onChange={(e) =>
                            setColumnsState((p) => ({ ...p, [id]: { show: e.target.checked } }))
                          }
                        />
                        <span>{String(c.title)}</span>
                      </label>
                    );
                  })}
                  <div className="px-2 pt-2 mt-1 border-t border-border">
                    <Button onClick={() => setColumnsPickerOpen(false)} className="w-full">
                      סיום
                    </Button>
                  </div>
                </div>
              }
            >
              <button
                type="button"
                title="ניהול עמודות"
                className="flex items-center justify-center w-9 h-9 bg-white rounded shadow-sm hover:bg-grey-bg transition-colors"
              >
                <ColumnsIcon />
              </button>
            </Popover>
          </Tooltip>
        </div>
      </div>

      {/* Branch view: say plainly that the amounts are a share of each line. */}
      {branchFilter != null && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={`מוצג החלק של ${branchLabel} בכל שורה`}
          description={
            <>
              <div>
                {branchFilter === NO_BRANCH
                  ? 'שכר שאינו שייך לסניף מסוים: שכר בסיס, נסיעות (למעט לפי ק"מ), בונוס מדרגות, בונוס המרות, בונוס חד-פעמי, הפחתה ושיעורים ללא סניף.'
                  : 'רק שכר שעבור שיעורים בסניף זה. שכר בסיס, בונוסים והפחתות מוצגים תחת "ללא שיוך לסניף".'}{' '}
                החישוב, השליחה, האישור והסגירה נשארים לפי יחידת רווח והפסד.
              </div>
              {unsplitCount > 0 && (
                <div className="mt-1">
                  {unsplitCount === 1 ? 'שורה אחת אינה מוצגת' : `${unsplitCount} שורות אינן מוצגות`}: אין להן פירוט
                  לפי סניף (נסגרו לפני שנוספה האפשרות). לצפייה בהן בחרו "כל הסניפים".
                </div>
              )}
            </>
          }
        />
      )}

      {/* Pay that reaches nobody — one line, details on demand. Kept visible
          (never dismissible): it's money no line in the report carries. */}
      {!isPartial && unmatchedStaff.length > 0 && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={`שיעורים בסך ${formatMoney(unmatchedPay)} אינם משויכים לאף איש צוות בשכר`}
          action={
            <Button size="small" type="link" onClick={() => setUnmatchedOpen((o) => !o)}>
              {unmatchedOpen ? 'הסתר פרטים' : 'הצג פרטים'}
            </Button>
          }
          description={
            unmatchedOpen ? (
              <>
                <div>
                  השיעורים האלה התקיימו ומשויכים בארבוקס לאנשי צוות שאינם ברשימת השכר, ולכן השכר
                  עליהם לא מופיע לאף אחד בדוח:
                </div>
                <ul className="list-disc mt-1" style={{ paddingInlineStart: 20 }}>
                  {unmatchedStaff.map((u) => (
                    <li key={u.arbox_users_boxes_id ?? u.name}>
                      {u.name || `איש צוות #${u.arbox_users_boxes_id}`} —{' '}
                      {u.class_count === 1 ? 'שיעור אחד' : `${u.class_count} שיעורים`},{' '}
                      {formatMoney(u.class_pay)}
                    </li>
                  ))}
                </ul>
                <div className="mt-1">
                  כדי לשלם להם, סמנו אותם "בשכר" בהגדרות שכר פרטניות. מי שאינו פעיל בארבוקס מוצג שם
                  לאחר סימון "הצג אנשי צוות שאינם פעילים בארבוקס".
                </div>
              </>
            ) : undefined
          }
        />
      )}

      <div ref={tableWrapperRef} className="flex-1 min-h-0 flex flex-col">
        {reportLoading ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <Loader size={48} />
            {liveProgress && (
              <span className="text-small text-secondary-text">
                {throughLabel ? `טוען שיעורים מ-Arbox… נטענו עד ${throughLabel}` : 'טוען נתונים מ-Arbox…'}
              </span>
            )}
          </div>
        ) : (
          <Table<PayrollReportRow>
          rowKey={rowKeyOf}
          size="small"
          showSorterTooltip={false}
          columns={visibleColumns}
          dataSource={rows}
          className={isPartial ? 'opacity-60' : undefined}
          rowSelection={
            isPartial
              ? undefined
              : {
                  selectedRowKeys: selectedKeys,
                  onChange: (keys) => setSelectedKeys(keys.map(String)),
                }
          }
          // Partial rows ARE the progress indicator — no overlay spinner on top.
          loading={
            reportQuery.isFetching && !isPartial ? { spinning: true, indicator: loaderIndicator('sm') } : false
          }
          scroll={{ x: scrollX, y: tableScrollY }}
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
          // Capture the post-filter dataset so the תכלת summary bar totals track
          // whatever is currently shown (across the full filtered set, all pages).
          onChange={(_pagination, _filters, _sorter, extra) =>
            setFilteredRows(extra.currentDataSource)
          }
          locale={{
            emptyText: (
              <EmptyState
                title="אין שכר להצגה בתקופה זו"
                description="לא נמצא שכר לתשלום לאנשי הצוות בתקופה שנבחרה (אנשי צוות עם שכר אפס מוסתרים)."
              />
            ),
          }}
        />
        )}

        {/* Sticky תכלת summary bar — mirrors the ArboxExpenses footer strip. Sits
            below the (internally-scrolling) table so it stays in view, and sums
            the rows currently on screen. */}
        {!reportLoading && rows.length > 0 && (
          <div style={{ background: '#DFF1FF' }} className="shrink-0">
            <div className="px-2 py-2 flex items-center justify-center flex-wrap gap-x-6 gap-y-1 text-small text-main-text">
              {isPartial && <span className="font-semibold text-secondary-text">חלקי…</span>}
              <span>
                כמות אנשי צוות <span className="font-semibold">{totalStaff}</span>
              </span>
              <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
              <span className="flex items-center gap-1">
                מע&quot;מ <span className="font-semibold"><Money value={totalVat} /></span>
              </span>
              <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
              <span className="flex items-center gap-1">
                סה&quot;כ <span className="font-semibold"><Money value={totalPay} /></span>
              </span>
              {totalDeduction > 0 && (
                <>
                  <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
                  <span className="flex items-center gap-1">
                    הפחתות{' '}
                    <span className="font-semibold text-error-red">
                      -<Money value={totalDeduction} />
                    </span>
                  </span>
                </>
              )}
              {/* With ניכוי מס במקור the bottom line the box actually transfers
                  differs from the invoiced total — show both, never just one. */}
              {totalWithholding > 0 && (
                <>
                  <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
                  <span className="flex items-center gap-1">
                    ניכוי במקור{' '}
                    <span className="font-semibold text-error-red">
                      -<Money value={totalWithholding} />
                    </span>
                  </span>
                  <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
                  <span className="flex items-center gap-1">
                    להעברה <span className="font-semibold"><Money value={totalNet} /></span>
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <PayrollBulkBar
        selected={isPartial ? [] : selectedRows}
        onSend={canSend ? confirmBulkSend : undefined}
        onExcel={() => exportPayrollCsv(selectedRows, periodLabel, boxMechanisms)}
        onPrint={() => exportPayrollPrint(selectedRows, periodLabel, boxMechanisms)}
        onClear={() => setSelectedKeys([])}
      />

      <PayrollDetailDrawer
        staffId={detailStaff?.id ?? null}
        staffName={detailStaff?.name ?? ''}
        selection={selection}
        unitId={detailStaff?.unitId}
        unitName={detailStaff?.unitName}
        invoiceCheckStatus={detailStaff?.invoiceCheckStatus ?? null}
        invoiceAmount={detailStaff?.invoiceAmount ?? null}
        invoiceCheckIssues={detailStaff?.invoiceCheckIssues ?? []}
        onClose={() => setDetailStaff(null)}
      />

      <RecomputeDiffModal
        open={recomputeOpen}
        selection={selection}
        diff={recomputeDiff}
        unitId={recomputeUnitId}
        onClose={() => setRecomputeOpen(false)}
      />

      <StaffSalaryEditDrawer
        open={salaryEditStaff !== null}
        staffId={salaryEditStaff?.id ?? null}
        staffName={salaryEditStaff?.name ?? ''}
        onClose={() => setSalaryEditStaff(null)}
        onSaved={() => {
          // Settings changes affect computed pay → refresh the report.
          void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
        }}
      />
    </div>
  );
}
